import { Box, Grid, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { NavigateFunction, useParams } from "react-router-dom";
import ordersService from "../../../services/Orders/OrdersService";
import officesService from "../../../services/Offices/OfficesService";
import CardDetails from "../../Components/CardDetails/CardDetails";
import DesignParameters from "../../Components/DesignParameters/DesignParameters";
import GridProperty from "../../Components/GridProperty/GridProperty";
import { Country, State, City } from 'country-state-city';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import storage from "../../../services/Share/storageWithEncryption";
import { UserRoles } from "../../../domain/Users/userRoles";
import { getErrorMessage } from "../../Components/Common/helper";

interface Props {
  navigate: NavigateFunction;
}

export default function OrderDetails({ navigate }: Props)
{
  const [order, setOrder] = useState<any>({});
  const [oldOrder, setOldOrder] = useState<any>({});
  const [office, setOffice] = useState<any>({});
  const [tabIndex, setTabIndex] = useState(0);
  const [editable, setEditable] = useState(false);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [validations, setValidations] = useState<any>({});
  const { orderId } = useParams();
  const userRole = storage.getDataFromLocalStorage("userRole");
  const remarkRoles = [UserRoles.MTLSEngineers, UserRoles.MotionAdmin];

  const countries = Country.getAllCountries();

  // Helper function to check if order code starts with "OS"
  const isOSOrder = (orderCode: string) => {
    return orderCode && orderCode.startsWith('OS');
  };

  useEffect(() => {
    ordersService.getOrderDetails(orderId!)
      .then((o: any) => {
        setOrder(o);
        // Don't fetch office details if order code starts with "OS"
        if (o.officeId && !isOSOrder(o.orderCode)) {
          officesService.getOffice(o.officeId.toString())
            .then((officeData: any) => {
              setOffice(officeData);
              // Set the shipment address from office address
              const updatedOrder = {
                ...o,
                address: officeData.address
              };
              setOrder(updatedOrder);
              setAddressCountry(updatedOrder);
            })
            .catch(error => {
              console.error('Error fetching office details:', error);
              setAddressCountry(o);
            });
        } else {
          setAddressCountry(o);
        }
      })
    },[orderId])

  useEffect(() => {
    if (editable && order.address) {
      setAddressCountry(order);
    }
  }, [order.address?.country, order.address?.state])

  function dateFormat(date: string, onlyDate?: boolean) {
    if(!date) return '';
    if(onlyDate)
      return new Date(date).toLocaleString('es', {day: '2-digit', month: '2-digit', year: 'numeric'});
    return new Date(date).toLocaleString('es',
      {day: '2-digit', month: '2-digit', year: 'numeric', hour:'2-digit', minute:'2-digit', second: '2-digit'});
  }

  function setAddressCountry(_order?: any) {
    const o = _order ?? order;
    const country = countries.find(f => f.name.toLowerCase() === o.address?.country?.toLowerCase());
    if (country) {
      const states = State.getStatesOfCountry(country.isoCode);
      setStates(states);
      const state = states.find(s => s.name.toLowerCase() === o.address?.state?.toLowerCase())
      if (state) {
        setCities(City.getCitiesOfState(country.isoCode, state.isoCode));
      } else {
        setCities([]);
      }
    } else {
      setStates([]);
      setCities([]);
    }
  }

  function handleAddressChange(value: any, fieldName: string) {
    setOrder({...order, address: {...order.address, [fieldName]: value} });
    setValidations({ ...validations, [fieldName[0].toLowerCase() + fieldName.slice(1)]: undefined });
  }

  function validationAddress() {
    return !!validations.street || !!validations.postalCode || !!validations.city || !!validations.state || !!validations.country;
  }

  function handleOrdersClick() {
    navigate("/Orders");
  }

  return (
    <Box style={{ width: "100vw-50px", padding: "0px 30px 30px 30px", marginLeft: 50, }} >
      <Box component="label" className="navigation-link" sx={{ textDecoration: "none" }} onClick={handleOrdersClick}>Orders</Box>
      <Box sx={{ display: 'flex', marginLeft: '2rem', marginBottom: '1rem', alignItems: 'center' }}>
        <Box sx={{ fontSize: '1.5rem' }}>
          <b>Case Details</b>
        </Box>
        <Box sx={{ fontSize: '1rem', alignContent: "center", marginLeft: '5rem' }}>
          <b>ID: {order.orderCode}</b>
        </Box>
        <Box sx={{ fontSize: '1rem', marginLeft: '5rem' }}>
            <b>Status:</b> {order.status?.replace(/([A-Z])/g, ' $1').trim()}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tabs className="custom-tabs" value={tabIndex} onChange={(_, e) => setTabIndex(e)} >
          <Tab className="custom-tab" label="General Info" />
          <Tab className="custom-tab" label="History" />
          <Tab className="custom-tab" label="Design Parameters" />
          <Tab className="custom-tab" label="Comments" />
          {!isOSOrder(order.orderCode) && (
            <Tab className="custom-tab" label="Shipment Address" sx={{ color: validationAddress() ? 'red !important' : null }} />
          )}
        </Tabs>
        <div style={{display: 'flex', flex: 1, justifyContent: 'end' }}>
          {!editable
            ? <IconButton style={{ marginRight: '10px', alignSelf: 'self-end' }} aria-label='edit'
                onClick={() => {setEditable(true); setOldOrder(order);}}
              >
                <EditIcon />
              </IconButton>
            : <IconButton style={{ marginRight: '10px', alignSelf: 'self-end' }} aria-label='cancel'
                onClick={() => {setEditable(false); setOrder(oldOrder);}}
              >
                <CloseIcon />
              </IconButton>
          }
        </div>
      </Box>

      {/* GENERAL INFO */}
      <div role="tabpanel" hidden={tabIndex !== 0} style={{ margin: 30 }}>
        <Grid container spacing={4}>
          <Grid item xs={4.5}>
            <CardDetails
              title="Practitioner"
              entity={{ Name: `${order.orderHistory?.[0].applicationUser.name} ${order.orderHistory?.[0].applicationUser.lastName}`, Delivery: order.office, '': '' }}
            />
          </Grid>
          <Grid item xs={4.5}>
            <CardDetails
              title="Patient"
              entity={{ 'Full Name': `${order.endCustomer?.firstName} ${order.endCustomer?.middleName} ${order.endCustomer?.lastName}`, Gender: order.endCustomer?.gender, Email: order.endCustomer?.email }}
            />
          </Grid>
          <Grid item xs={3} />
          <Grid item xs={4.5}>
            <CardDetails
              title="Product"
              entity={{ Name: order.product?.name, Type: order.product?.productType}}
            />
          </Grid>
          <Grid item xs={4.5}>
            <CardDetails
              title="Schedule"
              entity={{'Order Created': dateFormat(order.dateOfCreation), 'Target Shipping Date': dateFormat(order.targetShippingDate, true), 'Shipped Date': dateFormat(order.shippedDate) }}
            />
          </Grid>
        </Grid>
      </div >

      {/* HISTORY */}
      <div role="tabpanel" hidden={tabIndex !== 1} style={{ margin: 30 }}>
        <Table aria-label="customized table">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell><b>Status</b></TableCell>
                {remarkRoles.includes(userRole) ? <TableCell><b>Reason</b></TableCell> : undefined}
                <TableCell><b>Date</b></TableCell>
                <TableCell><b>User</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
            {
              order.orderHistory?.map((o: any, i : number) => (
                <TableRow key={i} style={ (i % 2) === 0 ? { backgroundColor: "#bcd9f73f" } : {}}>
                  <TableCell style={{ paddingLeft: 30, borderBottom: 'none' }} ><b>{i+1}</b></TableCell>
                  <TableCell className="custom-table-cell" >{o.status.replace(/([A-Z])/g, ' $1').trim()}</TableCell>
                  {remarkRoles.includes(userRole) ? <TableCell className="custom-table-cell" >{o.reason}</TableCell> : undefined}
                  <TableCell className="custom-table-cell" >{dateFormat(o.date)}</TableCell>
                  <TableCell className="custom-table-cell" >{o.applicationUser ? `${o.applicationUser.name} ${o.applicationUser.lastName}` : 'System'}</TableCell>
                </TableRow>
                ))
            }
            </TableBody>
          </Table>
      </div >

      {/* DESIGN PARAMETERS */}
      <div role="tabpanel" hidden={tabIndex !== 2} style={{ margin: 30 }}>
        {order.designParameters && <DesignParameters
          parameters={order.designParameters.reduce((acc: any, current: any) => {
            acc[current.key] = current.value;
            return acc;
          }, {})}
        />}
      </div>

      {/* REMARKS */}
      <div role="tabpanel" hidden={tabIndex !== 3} style={{ margin: 30, maxWidth: '800px' }}>
        <Table aria-label="customized table">
          <TableHead>
            <TableRow>
              <TableCell><b>User</b></TableCell>
              <TableCell><b>Date</b></TableCell>
              <TableCell><b>Type</b></TableCell>
              <TableCell><b>Comment</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {
            order.comments?.map((o: any, i : number) => (
              <TableRow key={i} style={ (i % 2) === 0 ? { backgroundColor: "#bcd9f73f" } : {}}>
                <TableCell className="custom-table-cell" >{`${o.applicationUser.name} ${o.applicationUser.lastName}`}</TableCell>
                <TableCell className="custom-table-cell" >{dateFormat(o.commentDate)}</TableCell>
                <TableCell className="custom-table-cell" >{o.commentType}</TableCell>
                <TableCell className="custom-table-cell" >{o.comment}</TableCell>
              </TableRow>
              ))
          }
          </TableBody>
        </Table>
      </div>

      {/* ADDRESS */}     
      {!isOSOrder(order.orderCode) && (
        <div role="tabpanel" hidden={tabIndex !== 4} style={{ margin: 30 }}>
          <Box sx={{ marginBottom: 2 }}>
            <Box sx={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: 1 }}>
              Shipment Address
            </Box>
   
          </Box>
          <Grid container spacing={1} sx={{ padding: '0 40px', marginTop: '0px' }}>
            <GridProperty labelText='Street Name' onChange={(value: string) => handleAddressChange(value, "streetName")} side={4}
              required={true} editable={editable} value={order.address?.streetName} placeholder='Enter Street Name'
              error={!!validations.street} helperText={getErrorMessage(validations.street)} />
            <GridProperty labelText='Appartment Number' onChange={(value: string) => handleAddressChange(value, "appartmentNumber")} side={4}
              required={false} editable={editable} value={order.address?.appartmentNumber} placeholder='Enter Appartment Number' />
            <GridProperty labelText='Postal Code' onChange={(value: string) => handleAddressChange(value, "postalCode")} side={4}
              required={true} editable={editable} value={order.address?.postalCode} placeholder='Enter Postal Code'
              error={!!validations.postalCode} helperText={getErrorMessage(validations.postalCode)} />
            <GridProperty labelText='Country' valueKey='name' freeSolo={false} required={true} editable={editable} side={4}
              value={order.address?.country} placeholder='Enter Country' selectValues={countries}
              error={!!validations.country} helperText={getErrorMessage(validations.country)}
              onChange={(value: string) => {
                const country = countries.find(f => f.name === value);
                setOrder({...order, address: {...order.address, country: value, countryCode: country?.phonecode, state: '', city: '' }});
                setValidations({ ...validations, country: undefined});
              }}/>
            <GridProperty labelText='State' valueKey='name' editable={editable} value={order.address?.state} placeholder='Enter State' selectValues={states} side={4}
              error={!!validations.state} helperText={getErrorMessage(validations.state)}
              onChange={(value: string) => {
                setOrder({...order, address: {...order.address, state: value, city: '' }});
                setValidations({ ...validations, state: undefined });
              }}/>
            <GridProperty labelText='City' valueKey='name' required={true} editable={editable} value={order.address?.city} placeholder='Enter City' selectValues={cities} side={4}
              error={!!validations.city} helperText={getErrorMessage(validations.city)}
              onChange={(value: string) => handleAddressChange(value, "city")} />
            <GridProperty labelText='Country Code' value={order.address?.countryCode} side={4} />
          </Grid>
        </div>
      )}
     
    </Box>
  )
}


